import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, PHOTO_GRADE, paletteTint } from "./types";

// venueCollage — a full 2×2 image grid with a fixed white title box across the
// middle. Three rows: a Bebas accent (top), an Anton/Impact bold title, and a
// Bebas tail (bottom). Accent + tail are large and overhang the box onto the
// photos, with a thick white stroke for legibility.
//   items[]       → up to 4 { imageUrl }
//   titleAccent   → top accent line (2 words incl. a number)
//   venue.name    → bold title (2 words; auto-shrinks for 3)
//   venue.tagline → tail line (2-3 words)
//   brand         → bottom pill
const INK = "#1c1714";
const BOX_H = 370; // fixed text-box height
const FRAME = 22;  // white frame thickness around the text box
const GAP = 10;    // thin grid gap between photos (also the lines that faintly
                   // show through the semi-transparent text box — kept slim)
const DEFAULT_TINT = "#f4efe9"; // soft off-white when no palette is detected
const BOX_ALPHA = 0.85; // box tint opacity — lets the photos faintly show through

// Convert a #rgb / #rrggbb tint to rgba() at the given alpha (returns the input
// unchanged if it's not a hex, so non-hex CSS colors still work).
function hexToRgba(hex: string, a: number): string {
  const m = /^#?([a-f0-9]{3}|[a-f0-9]{6})$/i.exec((hex || "").trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export function venueCollageTemplate(input: PinInput): string {
  const { brand, typography, venue, items, titleAccent } = input;
  const cells = (items ?? []).filter((i) => i.imageUrl).slice(0, 4);
  if (!cells.length) {
    throw new Error("venueCollage template requires items[] with at least one imageUrl");
  }
  const title = (venue?.name || input.title || "").trim();
  const accent = (titleAccent || "").trim();
  const tail = (venue?.tagline || "").trim();

  const MAXW = 940;
  const fit = (s: string, max: number, em: number): number => {
    const n = (s || "").length || 1;
    return n * em * max > MAXW ? Math.max(64, Math.floor(MAXW / (n * em))) : max;
  };
  // titleOnly = an advice/sentence collage (no "Weddings at" accent or location
  // tail). Those wrap to as many lines as needed at a BIG target size so the text
  // FILLS the box, instead of shrinking to a small 2-line block.
  const titleOnly = !accent && !tail;

  let titleLines: string[];
  let mainSize: number;
  let boxH: number;
  let rowGap = 50;
  let accSize = 0;
  let tailSize = 0;

  if (titleOnly) {
    const target = 156; // px target; lines wrap to keep the font this large
    const cpl = Math.max(6, Math.floor(MAXW / (target * 0.46))); // chars/line at target
    const words = title.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let cur = "";
    for (const w of words) { const nx = cur ? `${cur} ${w}` : w; if (cur && nx.length > cpl) { lines.push(cur); cur = w; } else cur = nx; }
    if (cur) lines.push(cur);
    titleLines = lines.slice(0, 5);
    const longest = titleLines.reduce((a, b) => (a.length >= b.length ? a : b), "");
    mainSize = Math.max(72, Math.min(target, Math.floor(MAXW / (Math.max(1, longest.length) * 0.46))));
    boxH = Math.round(titleLines.length * mainSize * 1.04 + 80);
  } else {
    // Venue-name titles: 3+ words (or a too-wide 2-word name) wrap to two BALANCED
    // lines; else one line. Accent + tail flank it.
    const titleWords = title.split(/\s+/).filter(Boolean);
    const oneLineTooWide = title.length * 0.46 * 170 + title.length > MAXW;
    const twoLine = titleWords.length >= 3 || (titleWords.length === 2 && oneLineTooWide);
    let split = 0;
    if (twoLine) {
      let best = Infinity;
      for (let k = 1; k < titleWords.length; k++) {
        const d = Math.abs(titleWords.slice(0, k).join(" ").length - titleWords.slice(k).join(" ").length);
        if (d < best) { best = d; split = k; }
      }
    }
    titleLines = twoLine ? [titleWords.slice(0, split).join(" "), titleWords.slice(split).join(" ")] : [title];
    const longestLine = titleLines.reduce((a, b) => (a.length >= b.length ? a : b), "");
    boxH = twoLine ? 470 : BOX_H;
    rowGap = twoLine ? 30 : 50;
    mainSize = fit(longestLine, twoLine ? 150 : 170, 0.46);
    // Accent (top) and tail (bottom) share one size cap so they overhang the box
    // symmetrically — making the gap accent→title equal to the gap title→tail.
    const flankCap = twoLine ? 188 : 210;
    accSize = fit(accent, flankCap, 0.42);
    tailSize = fit(tail, flankCap, 0.42);
  }
  const mainLH = titleLines.length > 1 ? 1.04 : 0.82;

  // Box interior tint: explicit bandTint override → palette word in the title →
  // soft default. The white FRAME stays uniform on all four sides.
  const boxTint = (input.bandTint && input.bandTint.trim()) || paletteTint(`${accent} ${title} ${tail}`, DEFAULT_TINT);
  // Semi-transparent so the 2×2 photos faintly continue behind the colored box.
  const boxBg = hexToRgba(boxTint, BOX_ALPHA);
  const ink = input.textColor || INK; // primary text color (override)

  const cellHtml = Array.from({ length: 4 }, (_, i) => {
    const c = cells[i];
    if (!c) return `<div class="cell empty"></div>`;
    return `<div class="cell"><div class="img" style="background-image:url('${escapeHtml(c.imageUrl as string)}')"></div></div>`;
  }).join("");

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Anton&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#fff}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:#fff}
  .grid{position:absolute;inset:0;display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:${GAP}px;background:#fff}
  .cell{position:relative;overflow:hidden;background:#efe9df}
  .cell.empty{background:linear-gradient(135deg,#efe9df,#e6ddcf)}
  .img{position:absolute;inset:0;background-size:cover;background-position:center;filter:${input.rawPhoto ? "none" : PHOTO_GRADE}}
  /* Title box — top edge raised 25px above centre. Uniform white frame on all
     four sides (border) with the interior painted in the palette/pastel tint. */
  .box{position:absolute;top:${PIN_HEIGHT / 2 - boxH / 2 - 25}px;left:0;right:0;height:${boxH + 25}px;z-index:3;background:${boxBg};border:${FRAME}px solid #fff;box-shadow:0 14px 44px rgba(0,0,0,.16)}
  /* Text stack centred over the box; accent + tail overhang onto the photos.
     50px gap between rows; tight line-height since these are all-caps. */
  .stack{position:absolute;top:50%;left:0;right:0;transform:translateY(-50%);z-index:4;display:flex;flex-direction:column;align-items:center;gap:${rowGap}px;text-align:center}
  .acc,.tail{font-family:'Bebas Neue',${typography.bodyFont},sans-serif;font-weight:400;line-height:.78;letter-spacing:1px;text-transform:uppercase;color:${ink};-webkit-text-stroke:20px #fff;paint-order:stroke}
  .acc{font-size:${accSize}px}
  .tail{font-size:${tailSize}px}
  .main{font-family:'Anton','Impact',${typography.bodyFont},sans-serif;font-weight:400;font-size:${mainSize}px;line-height:${mainLH};letter-spacing:1px;text-transform:uppercase;color:${ink}}
  /* Brand pill, bottom-centre. */
  .pill{position:absolute;bottom:42px;left:50%;transform:translateX(-50%);z-index:5;background:#fff;border-radius:999px;
    padding:12px 30px;box-shadow:0 8px 24px rgba(0,0,0,.22);
    font-size:24px;letter-spacing:4px;text-transform:uppercase;font-weight:700;color:${ink};white-space:nowrap}
</style></head>
<body><div class="pin">
  <div class="grid">${cellHtml}</div>
  <div class="box"></div>
  <div class="stack">
    ${accent ? `<div class="acc">${escapeHtml(accent)}</div>` : ""}
    ${title ? `<div class="main">${titleLines.map((l) => escapeHtml(l)).join("<br>")}</div>` : ""}
    ${tail ? `<div class="tail">${escapeHtml(tail)}</div>` : ""}
  </div>
  ${brand ? `<div class="pill">${escapeHtml(brand)}</div>` : ""}
</div></body></html>`;
}
